import { Store } from 'redux';
import { MPlayerState, UserInteractionData } from '../../../common/store/store.types';
import { StoreSubscriber } from '../../../common/store/storeSubscriber';
import { userInteractionSelector } from '../../../common/store/selectors/userInteraction.selector';
import { playerDataSelector } from '../../../common/store/selectors/playerData.selector';
import {
  DISCOVERY_SHOW_NEXT_VIDEO_IN_TOTAL_SECONDS,
  DISCOVERY_SHOW_SKIPPABLE_CONTENT_DEFAULT_SECONDS,
  DISCOVERY_SHOW_SKIPPABLE_CONTENT_IN_TOTAL_SECONDS,
  DISCOVERY_SHOW_SKIPPABLE_CONTENT_KEEP_WATCHING_DEFAULT_SECONDS,
} from '../discoveryPlugin.constants';
import { updateShowSkippableContent } from '../store/discoveryPlugin.actions';
import { discoverySelector } from '../../../common/store/selectors/discovery.selector';

export class SkippableContentHandler {
  private readonly store: Store<MPlayerState>;

  private userInteractionSubscriber: StoreSubscriber;

  private currentVideoTimeSubscriber: StoreSubscriber;

  private isUserInteract: boolean;

  private isFirstPlay = true;

  private showSkippableContent: boolean;

  public static createInstance(store: Store): SkippableContentHandler {
    return new SkippableContentHandler(store);
  }

  constructor(store: Store) {
    this.store = store;
    this.userInteractionSubscriber = new StoreSubscriber(store, SkippableContentHandler.getUserInteractionDependencies, this.onUserInteractionChanged.bind(this));
    this.currentVideoTimeSubscriber = new StoreSubscriber(store, SkippableContentHandler.getCurrentVideoTimeDependencies, this.onCurrentVideoTimeChanged.bind(this));
    this.isUserInteract = false;
    this.showSkippableContent = false;
  }

  private static getUserInteractionDependencies(state: MPlayerState): Array<UserInteractionData> {
    return [
      userInteractionSelector.userInteractionData(state),
    ];
  }

  private onUserInteractionChanged = (state: MPlayerState): void => {
    const { dispatch } = this.store;
    const userInteractionType = userInteractionSelector.userInteractionType(state);
    const playbackMethod = playerDataSelector.playbackMethod(state);
    const showSkippableContent = discoverySelector.showSkippableContent(state);
    const clickToPlayMode = playbackMethod === 'none';
    this.isUserInteract = !clickToPlayMode || !this.isFirstPlay;
    if (this.isUserInteract && showSkippableContent) {
      updateShowSkippableContent(false)(dispatch);
    }
    if (this.isFirstPlay && userInteractionType === 'play') {
      this.isFirstPlay = false;
    }
  };

  private static getCurrentVideoTimeDependencies(state: MPlayerState): Array<number> {
    return [
      playerDataSelector.currentVideoTime(state),
    ];
  }

  private onCurrentVideoTimeChanged = (state: MPlayerState): void => {
    const { dispatch } = this.store;
    const currentVideoTime = playerDataSelector.currentVideoTime(state);
    const playerSize = playerDataSelector.playerSize(state);
    const nextVideo = discoverySelector.nextVideo(state);
    const skippableContentTime = discoverySelector.skippableContentTime(state);

    const showOnlyKeepWatchingButtons = nextVideo === 'skippableContent' && playerSize === 'small';
    const keepWatchingButtonsTime = skippableContentTime === DISCOVERY_SHOW_SKIPPABLE_CONTENT_DEFAULT_SECONDS ? DISCOVERY_SHOW_SKIPPABLE_CONTENT_KEEP_WATCHING_DEFAULT_SECONDS : skippableContentTime;

    const startTimeShowSkippable = showOnlyKeepWatchingButtons ? keepWatchingButtonsTime : skippableContentTime;
    const totalTimeShowSkippable = showOnlyKeepWatchingButtons ? DISCOVERY_SHOW_NEXT_VIDEO_IN_TOTAL_SECONDS : DISCOVERY_SHOW_SKIPPABLE_CONTENT_IN_TOTAL_SECONDS;
    if (currentVideoTime >= startTimeShowSkippable && !this.isUserInteract && !this.showSkippableContent
      && currentVideoTime < startTimeShowSkippable + totalTimeShowSkippable) {
      updateShowSkippableContent(true)(dispatch);
      this.showSkippableContent = true;
    }
    if (this.isUserInteract) {
      updateShowSkippableContent(false)(dispatch);
    }
    if (currentVideoTime === 0) {
      this.isUserInteract = false;
      this.showSkippableContent = false;
      updateShowSkippableContent(false)(dispatch);
    }
  };
}
